import { APP_ROUTES } from "../constants";
import { PhoneNumberPage } from "../pages/PhoneNumberPage";
import { LoadingPage } from "../pages/LoadingPage";
import { WelcomePage } from "../pages/WelcomePage";
import { PayoutClaimEntryPage } from "../pages/PayoutClaimEntryPage";
import { IntentionLaterPage } from "../pages/IntentionLaterPage";
import { IntentionDiscardPage } from "../pages/IntentionDiscardPage";
import { AuthPage } from "../pages/AuthPage";
import { SsoCallbackPage } from "../pages/SsoCallbackPage";
import { createElement } from "react";
import { Navigate } from "react-router-dom";
import { TransactionsPage } from "../pages/TransactionsPage";
import { ReceiptSummaryPage } from "../pages/ReceiptSummaryPage";
import { TransferPage } from "../pages/TransferPage";
import { AddCardPage } from "../pages/AddCardPage";
import { AddCardProcessingPage } from "../pages/AddCardProcessingPage";
import { SendingMoneyPage } from "../pages/SendingMoneyPage";
import { OfacDisambiguationPage } from "../pages/OfacDisambiguationPage";
import { TermDetailPage } from "../pages/TermDetailPage";
import { SmsTermsPage } from "../pages/SmsTermsPage";
import { LegalPage } from "../pages/LegalPage";
import { SupportPage } from "../pages/SupportPage";
import { ErrorPage } from "../shared/error/pages/ErrorPage";
import { SessionExpiredPage } from "../shared/error/pages/SessionExpiredPage";
import { ClaimBlockedPage } from "../shared/error/pages/ClaimBlockedPage";
import { ServiceUnavailablePage } from "../shared/error/pages/ServiceUnavailablePage";
import { MaintenancePage } from "../shared/error/pages/MaintenancePage";

const CashDepositRedirect = () =>
  createElement(Navigate, { to: APP_ROUTES.TRANSACTIONS_PAGE, replace: true });

export interface IAppRoutes {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: any;
}

export const routesList: IAppRoutes[] = [
  { path: APP_ROUTES.PHONE_PAGE, Component: PhoneNumberPage },
  // Magic-link entry: the flow is encoded in the linkId itself
  { path: APP_ROUTES.LOADING_PAGE, Component: LoadingPage },
  { path: "/:linkId", Component: LoadingPage },
  { path: APP_ROUTES.WELCOME_PAGE, Component: WelcomePage },
  { path: APP_ROUTES.CLAIM_ENTRY_PAGE, Component: PayoutClaimEntryPage },
  { path: APP_ROUTES.LATER_PAGE, Component: IntentionLaterPage },
  { path: APP_ROUTES.DISCARD_PAGE, Component: IntentionDiscardPage },
  { path: APP_ROUTES.AUTH_PAGE, Component: AuthPage },
  { path: APP_ROUTES.SSO_CALLBACK_PAGE, Component: SsoCallbackPage },
  { path: APP_ROUTES.TRANSACTIONS_PAGE, Component: TransactionsPage },
  {
    path: `${APP_ROUTES.RECEIPT_PAGE}/:operationId`,
    Component: ReceiptSummaryPage,
  },
  { path: APP_ROUTES.CASH_DEPOSIT_PAGE, Component: CashDepositRedirect },
  { path: APP_ROUTES.TRANSFER_PAGE, Component: TransferPage },
  { path: APP_ROUTES.ADD_CARD_PAGE, Component: AddCardPage },
  { path: APP_ROUTES.ADD_CARD_PROCESSING_PAGE, Component: AddCardProcessingPage },
  { path: APP_ROUTES.SENDING_MONEY_PAGE, Component: SendingMoneyPage },
  {
    path: `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/:operationId`,
    Component: OfacDisambiguationPage,
  },
  { path: APP_ROUTES.TERM_DETAIL_PAGE, Component: TermDetailPage },
  { path: APP_ROUTES.SMS_TERMS_PAGE, Component: SmsTermsPage },
  { path: APP_ROUTES.LEGAL_PAGE, Component: LegalPage },
  { path: APP_ROUTES.SUPPORT_PAGE, Component: SupportPage },
  { path: APP_ROUTES.ERROR_PAGE, Component: ErrorPage },
  { path: APP_ROUTES.SESSION_EXPIRED_PAGE, Component: SessionExpiredPage },
  { path: APP_ROUTES.CLAIM_BLOCKED_PAGE, Component: ClaimBlockedPage },
  {
    path: APP_ROUTES.SERVICE_UNAVAILABLE_PAGE,
    Component: ServiceUnavailablePage,
  },
  {
    path: APP_ROUTES.MAINTENANCE_PAGE,
    Component: MaintenancePage,
  },
];

/**
 * Normalizes a raw numeric string into a `intPart.decimals` money string with
 * exactly two decimals. Returns an empty string for empty/zero-only input.
 */
export const formatMoney = (value: string) => {
  if (!value || value === ".00") return "";
  value = value.replace(/[^\d.]/g, "");
  const [intPart, decPart = ""] = value.split(".");
  const decimals = (decPart + "00").slice(0, 2);
  return `${intPart || "0"}.${decimals}`;
};

export type amountFormatterProps = (
  amount: number,
  currency?: string,
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
) => string;

/**
 * Formats a numeric amount using `Intl.NumberFormat` (decimal style, 2 decimals
 * by default). Separators are always `en-US`, independent of UI language.
 */
export const amountFormatter: amountFormatterProps = (
  amount,
  currency = "USD",
  {
    country = "en-US",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {}
) =>
  new Intl.NumberFormat(country, {
    style: "decimal",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);

/** Decimal amount + ISO code; USD also includes `$`. */
export function formatLabeledAmount(
  amount: number,
  currency = "USD",
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const formatted = amountFormatter(amount, currency, numberFormatOptions);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
}

/** Hero typography: en-US `25.00` split; `$` + ISO for USD, ISO suffix otherwise. */
export function formatHeroAmount(amount: number, currency: string) {
  const formatted = amountFormatter(amount, currency, {
    country: "en-US",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [intPart, decPart = "00"] = formatted.split(".");
  const code = currency.toUpperCase();
  return {
    prefix: code === "USD" ? "$" : "",
    suffix: ` ${code}`,
    intPart,
    decPart,
  };
}
